import { ReactNode, createContext, useContext, useState } from 'react'
import { Form } from '$components'
import { z } from 'zod'
import dayjs from 'dayjs'
import { CreateAppointmentFormControls } from './controls'
import { CreateAppointmentFormContent } from './content'

export enum CreateAppointmentQueryParam {
  SlotPersonId = 'spid',
}

export enum Input {
  Client = 'client',
  Pet = 'pet',
  Services = 'services',
  Date = 'date',
  Notes = 'notes',
  Slot = 'slot',
  Duration = 'duration',
  Price = 'price',
  StartTime = 'start_time',

  _NewClient = '_new_client',
  _NewPet = '_new_pet',

  NewClientFirstName = 'new_client_first_name',
  NewClientLastName = 'new_client_last_name',
  NewClientEmail = 'new_client_email',
  NewClientPhone = 'new_client_phone',
  NewPetName = 'new_pet_name',
  NewPetBreed = 'new_pet_breed',
  NewPetSizeCategory = 'new_pet_size_category',
  NewPetCoatType = 'new_pet_coat_type',
}

export type CreateAppointmentSchema = z.infer<typeof createAppointmentSchema>

export const createAppointmentSchema = z.object({
  [Input.Client]: z.object({
    id: z.number(),
    value: z.string(),
    image: z.string().nullish(),
  }, { message: 'Please select a client' }).nullable(),
  [Input.Pet]: z.object({
    id: z.string(),
    value: z.string(),
    image: z.string().nullish(),
  }, { message: 'Please select a pet' }).nullable(),
  [Input.Services]: z.object({
    id: z.number(),
    value: z.string(),
  }, { message: 'Please select a service' }).array(),
  [Input.Notes]: z.string().optional(),
  [Input.Date]: z.string(),
  [Input.Slot]: z.object({
    id: z.number(),
    start_at: z.string(),
    finish_at: z.string(),
    groomer: z.object({
      id: z.number().or(z.string()),
      first_name: z.string(),
      last_name: z.string(),
    }),
  }, { message: 'Please select an available slot' }),
  [Input.Duration]: z.number(),
  [Input.Price]: z.number(),
  [Input._NewClient]: z.boolean().optional(),
  [Input._NewPet]: z.boolean().optional(),
  [Input.NewClientFirstName]: z.string().optional(),
  [Input.NewClientLastName]: z.string().optional(),
  [Input.NewClientEmail]: z.string().email().optional(),
  [Input.NewClientPhone]: z.string().optional(),
  [Input.NewPetName]: z.string().optional(),
  [Input.NewPetBreed]: z.object({
    id: z.string(),
    value: z.string(),
  }).optional(),
  [Input.NewPetSizeCategory]: z.object({ id: z.string(), value: z.string() }).optional(),
  [Input.NewPetCoatType]: z.object({ id: z.string(), value: z.string() }).optional(),
}).refine((data) => {
  // If creating new client, require new client fields
  if (data[Input._NewClient]) {
    return !!(data[Input.NewClientFirstName] && data[Input.NewClientLastName] && data[Input.NewClientEmail] && data[Input.NewClientPhone])
  }
  // If not creating new client, require existing client
  return !!data[Input.Client]
}, {
  message: 'Please provide client information',
  path: [Input.Client],
}).refine((data) => {
  // If creating new pet, require new pet fields
  if (data[Input._NewPet]) {
    return !!(data[Input.NewPetName] && data[Input.NewPetBreed]?.value)
  }
  // If not creating new pet, require existing pet
  return !!data[Input.Pet]
}, {
  message: 'Please provide pet information',
  path: [Input.Pet],
})

type CreateAppointmentFormContextType = {
  onSubmit: (values: CreateAppointmentSchema) => void
  onCancel: () => void
  isLoading?: boolean
  defaultValues?: Partial<CreateAppointmentSchema>
  clientId?: number
  setClientId: (id?: number) => void
}

const CreateAppointmentFormContext = createContext<CreateAppointmentFormContextType | undefined>(undefined)

type CreateAppointmentFormProps = {
  onSubmit: (values: CreateAppointmentSchema) => void
  onCancel: () => void
  defaultValues?: Partial<CreateAppointmentSchema>
  isLoading?: boolean
  children?: ReactNode
}

export function CreateAppointmentForm({
  onSubmit,
  onCancel,
  defaultValues = {},
  isLoading = false,
  children,
}: CreateAppointmentFormProps) {
  const [clientId, setClientId] = useState<number | undefined>(defaultValues.client?.id)
  const today = dayjs.utc()

  const context: CreateAppointmentFormContextType = {
    onSubmit,
    onCancel,
    isLoading,
    defaultValues,
    clientId,
    setClientId,
  }

  return (
    <CreateAppointmentFormContext.Provider value={context}>
      <Form<CreateAppointmentSchema, typeof createAppointmentSchema>
        schema={createAppointmentSchema}
        onSubmit={onSubmit}
        defaultValues={{
          date: today.toISOString(),
          ...defaultValues,
        }}
      >
        {children}
      </Form>
    </CreateAppointmentFormContext.Provider>
  )
}

CreateAppointmentForm.Content = CreateAppointmentFormContent
CreateAppointmentForm.Controls = CreateAppointmentFormControls

export function useCreateAppointmentForm() {
  const context = useContext(CreateAppointmentFormContext)

  if (context === undefined) {
    throw new Error('useCreateAppointmentForm must be used within a CreateAppointmentFormProvider')
  }

  return context
}
